import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Sheet } from '@/app/_components/Sheet';
import { ELButton, ELLoader, ELSelect, type ELSelectOptionType } from '@davincihealthcare/elty-design-system-react';
import Modal from '@/app/_components/modal';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { RecapSection } from '@/app/_components/widgets/listing/card/_components/RecapSection';
import { useAvailabilitySlots } from '@/app/_components/widgets/listing/_hooks/useAvailabilitySlots';
import { SlotAvailability } from '@/app/_components/widgets/listing/types';
import { TimeButton } from '@/app/_components/widgets/listing/card/_components/TimeButton';
import { getDayTitle } from '@/app/_components/widgets/listing/_utils/time';
import { useIsTablet } from '@/app/_hooks/useIsScreen';
import { formatDate } from '@/utils/dates';
import { Doctor } from '@/types/algolia-types';
import { getDoctorName } from '@/utils/reservation';
import { SelectDoctorForTabletAndSmaller } from '@/app/_components/widgets/listing/card/_components/SelectDoctorForTabletAndSmaller';
import { trackAddToCart } from '@/utils/trackingMeta-pixelV2';
import { getCookieByName } from '@/utils/cookies';
import { usePatient } from '@/app/_hooks/usePatient';

export interface AvailabilityModalProps {
  doctors?: Doctor[];
  doctorId?: string;
  firstDayExpected?: number | null;
  initialSelectedDay: number | 'OTHER' | null;
  specialityId: string;
  workgroupId: string;
  onClose: () => void;
  onSubmit: (selectedSlot: SlotAvailability) => void | Promise<void>;
  /** If > 0, minimum startDate for availability requests is now + delayAvailability (ms) */
  delayAvailability?: number;
}

const scrollToElement = (id: string) => {
  setTimeout(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 50);
};

export const AvailabilityModal: FC<AvailabilityModalProps> = ({
  doctors = [],
  doctorId,
  firstDayExpected,
  initialSelectedDay,
  specialityId,
  workgroupId,
  onClose,
  onSubmit,
  delayAvailability,
}) => {
  const { patientEmail } = usePatient();
  const [selectedSlot, setSelectedSlot] = useState<SlotAvailability | null>(null);
  const [hasBeenInitialized, setHasBeenInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(doctorId);
  const [doctorIdsWithAvailability, setDoctorIdsWithAvailability] = useState<Set<string>>(new Set());
  const [hasCheckedDoctorAvailability, setHasCheckedDoctorAvailability] = useState(false);

  const [prevDoctorId, setPrevDoctorId] = useState(selectedDoctorId);
  if (prevDoctorId !== selectedDoctorId) {
    setPrevDoctorId(selectedDoctorId);
    setSelectedSlot(null);
    setHasBeenInitialized(false);
  }

  const { allSlots, groupedDays, sortedDates, isInitialLoading, fetchNextPage, isLoadingMore, hasMoreData } = useAvailabilitySlots({
    specialityId,
    workgroupId,
    firstAvailabilityDate: firstDayExpected,
    selectedDoctorId,
    delayAvailability,
  });

  const isTabletAndSmaller = useIsTablet();
  const showSelectDoctor = useMemo(() => {
    return doctors.length > 1 && doctors.some(doctor => doctor.isNameVisible);
  }, [doctors]);

  /*
    Wait for the first load to complete
    Scroll to the selected day or the first available slot if initialSelectedDay is an existing day
    Scroll to the "show more times" button if initialSelectedDay is OTHER and there are slots available, fetch also additional slots
  */
  useEffect(() => {
    // If the component has been initialized or there are no slots, do nothing
    if (hasBeenInitialized || sortedDates.length === 0 || isInitialLoading) return;

    if (initialSelectedDay && groupedDays[initialSelectedDay]) {
      const firstSlot = groupedDays[initialSelectedDay][0];
      if (firstSlot) {
        setSelectedSlot(firstSlot);
        scrollToElement(`day-title-${initialSelectedDay}`);
      }
    }

    if (!initialSelectedDay) {
      const firstDay = sortedDates[0];

      if (firstDay && groupedDays[firstDay]) {
        setSelectedSlot(groupedDays[firstDay][0]);
      }
    }

    setHasBeenInitialized(true);
  }, [initialSelectedDay, groupedDays, sortedDates.length, hasBeenInitialized, fetchNextPage, isInitialLoading, sortedDates]);

  useEffect(() => {
    if (hasCheckedDoctorAvailability || isInitialLoading || !showSelectDoctor || selectedDoctorId) return;

    const doctorIdsFromAllAvailabilities = new Set(allSlots.map(slot => slot.doctorId));
    setDoctorIdsWithAvailability(doctorIdsFromAllAvailabilities);
    setHasCheckedDoctorAvailability(true);
  }, [allSlots, hasCheckedDoctorAvailability, isInitialLoading, selectedDoctorId, showSelectDoctor]);

  const handleLoadMore = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);

  const handleSubmit = useCallback(async () => {
    if (!selectedSlot) return;

    setIsSubmitting(true);

    try {
      await onSubmit(selectedSlot);

      await trackAddToCart({
        fbc: getCookieByName('_fbc') || '',
        fbp: getCookieByName('_fbp') || '',
        userEmail: patientEmail,
        price: selectedSlot.price || 0,
        service: selectedSlot.id,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedSlot, onSubmit, patientEmail]);

  const continueButton = (
    <ELButton
      label="Continua"
      variant="filled"
      color="primary"
      trailingIcon={<ArrowRightIcon />}
      size="large"
      disabled={!selectedSlot || isSubmitting}
      onClick={handleSubmit}
      fullRadius
    />
  );

  const selectDoctorOptions = useMemo<ELSelectOptionType[]>(() => {
    return [
      { value: 'Tutti i professionisti sanitari', id: undefined },
      ...doctors.map(doctor => ({
        value: getDoctorName(doctor),
        id: doctor.id,
        disabled: hasCheckedDoctorAvailability && !doctorIdsWithAvailability.has(doctor.id),
      })),
    ];
  }, [doctors, hasCheckedDoctorAvailability, doctorIdsWithAvailability]);

  const handleSelectDoctor = useCallback((option: ELSelectOptionType) => {
    setSelectedDoctorId(option.id);
  }, []);

  const doctorSelectLargeScreen = showSelectDoctor && (
    <div className="flex flex-col gap-1">
      <ELSelect
        name="doctors"
        label="Professionista sanitario"
        options={selectDoctorOptions}
        onSelect={handleSelectDoctor}
        initialSelectedItem={selectDoctorOptions.find(opt => opt.id === selectedDoctorId)}
        data-cy="doctors-select"
      />
    </div>
  );

  const dayList = (
    <div className="flex flex-col gap-6">
      {isInitialLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <ELLoader />
        </div>
      ) : (
        <>
          {sortedDates.map(timestamp => {
            const slots = groupedDays[timestamp];
            const dayTitle = getDayTitle(timestamp);
            return (
              <div key={timestamp} className="flex flex-col gap-3">
                <h3 id={`day-title-${timestamp}`} className="font-semibold capitalize">
                  {dayTitle}
                </h3>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(70px,1fr))] gap-2" role="radiogroup">
                  {slots.map(slot => {
                    const timeTitle = formatDate({
                      date: new Date(slot.start),
                      localeOptions: { hour: '2-digit', minute: '2-digit' },
                    });

                    return (
                      <TimeButton
                        key={slot.start}
                        dataCy={`slot-button`}
                        dataTimestamp={slot.start}
                        title={timeTitle}
                        isSelected={selectedSlot?.start === slot.start}
                        onClick={() => setSelectedSlot(slot)}
                        customAriaLabel={`Giorno ${dayTitle}, orario ${timeTitle}`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex w-full flex-col items-center gap-2 md:flex-row">
            <ELButton
              id="show-more-times"
              aria-label={hasMoreData ? 'Mostra altri orari' : 'Nessun altro orario disponibile'}
              label={hasMoreData ? 'Mostra altri orari' : 'Nessun altro orario disponibile'}
              variant="text"
              disabled={isInitialLoading || isLoadingMore || !hasMoreData}
              color="primary"
              size="small"
              onClick={handleLoadMore}
              fullRadius
            />
            {isLoadingMore && <ELLoader />}
          </div>
        </>
      )}
    </div>
  );

  if (isTabletAndSmaller) {
    return (
      <Modal isModalOpen onClose={onClose} className="m-0 h-full max-h-full w-full max-w-full px-4 pt-5">
        <div className="flex h-full max-h-full w-full max-w-full flex-col gap-6">
          <header className="grid grid-cols-[1.5rem_1fr_1.5rem] items-center justify-end text-right">
            <div /> {/*Future back button*/}
            <h2 className="text-center text-base font-bold text-primary">Scelta orario</h2>
            <button aria-label="Chiudi" className="focus:outline-none" onClick={onClose}>
              <XMarkIcon className="w-full " />
            </button>
          </header>

          <RecapSection selectedSlot={selectedSlot} />

          {doctors.length > 1 && (
            <SelectDoctorForTabletAndSmaller
              selectedDoctorId={selectedDoctorId}
              selectDoctorOptions={selectDoctorOptions}
              onAcceptSelection={setSelectedDoctorId}
            />
          )}

          <div className="scrolling-shadow flex-1 overflow-y-auto pb-28">{dayList}</div>

          <div className="fixed bottom-0 left-0 w-full px-4 [&>button]:w-full">
            <div className="h-6 w-full bg-gradient-to-t from-white via-white/90 to-transparent" />
            {continueButton}
            <div className="h-6 w-full bg-gradient-to-b from-white via-white/90 to-transparent" />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Sheet isOpen={true} onClose={onClose}>
      <div className="relative flex h-full max-h-full flex-col gap-6 px-20 pb-5 pt-10">
        <h2 className="text-2xl font-bold text-primary">Scelta orario</h2>
        <RecapSection selectedSlot={selectedSlot} />
        {doctorSelectLargeScreen}
        <div className="scrolling-shadow flex-1 overflow-y-auto">{dayList}</div>
        <div className="w-fit">{continueButton}</div>
      </div>
    </Sheet>
  );
};
